import { useState, useEffect } from 'react'
import { supabase, SUPABASE_ANON_KEY } from '../supabaseClient'
import { C, st, formatDate } from '../theme'
import { EmptyState, Modal, ScrollTable, Pagination, PAGE_SIZE } from './UI'

const PLANS = [
  { key: 'trial',        label: '試用中',   color: C.yellow,  bg: C.yellowBg },
  { key: 'starter',      label: '入門方案', color: C.accent,  bg: C.accentDim },
  { key: 'professional', label: '專業方案', color: '#7c3aed', bg: '#ede9fe' },
  { key: 'enterprise',   label: '企業方案', color: C.green,   bg: C.greenBg },
  { key: 'suspended',    label: '已停用',   color: C.red,     bg: C.redBg },
]
const planInfo = k => PLANS.find(p => p.key === k) || PLANS[0]

const ALL_MODULES = [
  { key: 'wms',         label: '倉儲管理',  icon: '📦' },
  { key: 'production',  label: '生產管理',  icon: '🏭' },
  { key: 'shipping',    label: '出貨管理',  icon: '🚚' },
  { key: 'crm',         label: '客戶管理',  icon: '👥' },
  { key: 'reports',     label: '報表分析',  icon: '📊' },
  { key: 'maintenance', label: '機械維修',  icon: '🔧' },
  { key: 'fives',       label: '5S管理',    icon: '🏆' },
  { key: 'tasks',       label: '任務管理',  icon: '📋' },
  { key: 'purchasing',  label: '採購管理',  icon: '🛒' },
  { key: 'attendance',  label: '出勤管理',  icon: '⏰' },
  { key: 'products',    label: '商品管理',  icon: '🗂️' },
]

const PLAN_MODULE_DEFAULTS = {
  trial:        ['wms', 'products'],
  starter:      ['wms', 'shipping', 'products'],
  professional: ['wms', 'shipping', 'products', 'crm', 'reports', 'purchasing'],
  enterprise:   ALL_MODULES.map(m => m.key),
}

const genPassword = () => {
  const c = 'abcdefghjkmnpqrstuvwxyz23456789'
  return Array.from({ length: 8 }, () => c[Math.floor(Math.random() * c.length)]).join('')
}

const emptyNewCustomer = () => ({
  name: '', tax_id: '', slug: '', short_code: '', contact_email: '', contact_phone: '', address: '', notes: '',
  plan: 'trial', max_users: 10,
  plan_expires_at: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().slice(0, 10),
  admin_name: '', admin_email: '', admin_password: genPassword(),
})

// ── 獨立後台 Portal（Super Admin 專屬）──
export default function SuperAdminPortal({ userProfile, onLogout }) {
  const [toast, setToast] = useState(null)
  const showToast = (msg, type = 'success') => { setToast({ msg, type }); setTimeout(() => setToast(null), 3500) }
  const [tab, setTab] = useState('companies')
  const [companies, setCompanies] = useState([])
  const [allUsers, setAllUsers] = useState([])
  const [allLogs, setAllLogs] = useState([])
  const [page, setPage] = useState(1)
  const [userPage, setUserPage] = useState(1)
  const [userTab, setUserTab] = useState(null)   // 展開的公司 id（用戶分組）

  const [showNewCustomer, setShowNewCustomer] = useState(false)
  const [newCustomer, setNewCustomer] = useState(emptyNewCustomer())
  const [creating, setCreating] = useState(false)
  const [createdResult, setCreatedResult] = useState(null)

  const [editingCompany, setEditingCompany] = useState(null)
  const [editForm, setEditForm] = useState({})
  const [savingEdit, setSavingEdit] = useState(false)

  const [selectedCompany, setSelectedCompany] = useState(null)
  const [companyUsers, setCompanyUsers] = useState([])
  const [companyLogs, setCompanyLogs] = useState([])

  const [companyModules, setCompanyModules] = useState({})
  const [moduleTab, setModuleTab] = useState(null)
  const [savingModules, setSavingModules] = useState(false)

  // 封存
  const [showArchived, setShowArchived] = useState(false)
  const [archivingCompany, setArchivingCompany] = useState(null)  // { id, name }
  const [archiveReason, setArchiveReason] = useState('')

  // 刪除客戶
  const [deletingCompany, setDeletingCompany] = useState(null)  // { id, name }
  const [deleteConfirmName, setDeleteConfirmName] = useState('')
  const [deleting, setDeleting] = useState(false)

  // 廣播通知
  const [broadcastForm, setBroadcastForm] = useState({ target: 'all', company_id: '', title: '', body: '' })
  const [broadcasting, setBroadcasting] = useState(false)

  // 代理登入
  const [impersonating, setImpersonating] = useState(false)

  // 維護排程
  const tomorrow8am = () => { const d = new Date(); d.setDate(d.getDate()+1); d.setHours(8,0,0,0); return d }
  const tomorrow8pm = () => { const d = new Date(); d.setDate(d.getDate()+1); d.setHours(20,0,0,0); return d }
  const toLocalDT = d => { const p = n => String(n).padStart(2,'0'); return `${d.getFullYear()}-${p(d.getMonth()+1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}` }
  const emptySchedule = () => ({ company_id: '', title: '系統維護公告', body: '', starts_at: toLocalDT(tomorrow8am()), ends_at: toLocalDT(tomorrow8pm()), contact_name: '', contact_phone: '', contact_email: '' })
  const [schedules, setSchedules] = useState([])
  const [showScheduleForm, setShowScheduleForm] = useState(false)
  const [editingSchedule, setEditingSchedule] = useState(null)   // null = 新增, object = 編輯
  const [scheduleForm, setScheduleForm] = useState(emptySchedule())
  const [scheduleRenotify, setScheduleRenotify] = useState(false)
  const [savingSchedule, setSavingSchedule] = useState(false)
  const sf = (k, v) => setScheduleForm(f => ({ ...f, [k]: v }))

  const openEditSchedule = (s) => {
    setScheduleForm({
      company_id:    s.company_id    || '',
      title:         s.title,
      body:          s.body          || '',
      starts_at:     toLocalDT(new Date(s.starts_at)),
      ends_at:       toLocalDT(new Date(s.ends_at)),
      contact_name:  s.contact_name  || '',
      contact_phone: s.contact_phone || '',
      contact_email: s.contact_email || '',
    })
    setScheduleRenotify(false)
    setEditingSchedule(s)
  }

  const nc = (k, v) => setNewCustomer(f => ({ ...f, [k]: v }))

  const load = async () => {
    const [{ data: comps }, { data: users }, { data: logs }, { data: mods }, { data: scheds }] = await Promise.all([
      supabase.from('companies').select('*').order('created_at', { ascending: false }),
      supabase.from('user_profiles').select('*').order('created_at', { ascending: false }),
      supabase.from('operation_logs').select('*').order('created_at', { ascending: false }).limit(200),
      supabase.from('company_modules').select('*'),
      supabase.from('maintenance_schedules').select('*').order('starts_at', { ascending: false }),
    ])
    if (comps) setCompanies(comps)
    if (users) setAllUsers(users)
    if (logs) setAllLogs(logs)
    if (scheds) setSchedules(scheds)
    if (mods) {
      const map = {}
      mods.forEach(m => {
        if (!map[m.company_id]) map[m.company_id] = {}
        map[m.company_id][m.module] = m.enabled
      })
      setCompanyModules(map)
    }
  }

  useEffect(() => { load() }, [])

  const loadCompanyDetail = async (company) => {
    setSelectedCompany(company)
    const [{ data: users }, { data: logs }] = await Promise.all([
      supabase.from('user_profiles').select('*').eq('company_id', company.id).order('created_at'),
      supabase.from('operation_logs').select('*').eq('company_id', company.id).order('created_at', { ascending: false }).limit(50),
    ])
    if (users) setCompanyUsers(users)
    if (logs) setCompanyLogs(logs)
  }

  const handleCreateNewCustomer = async () => {
    const { name, slug, short_code, admin_name, admin_email, admin_password, plan } = newCustomer
    if (!name.trim()) { showToast('請填寫公司名稱', 'error'); return }
    if (!slug.trim()) { showToast('請填寫識別碼', 'error'); return }
    if (!short_code.trim()) { showToast('請填寫員工登入代碼', 'error'); return }
    if (!/^[a-z0-9]{2,12}$/.test(short_code.trim())) { showToast('員工登入代碼只能英文小寫+數字，2~12字元', 'error'); return }
    if (!admin_name.trim()) { showToast('請填寫管理員姓名', 'error'); return }
    if (!admin_email.trim()) { showToast('請填寫管理員 Email', 'error'); return }
    if (!admin_password || admin_password.length < 6) { showToast('預設密碼至少 6 碼', 'error'); return }

    setCreating(true)
    try {
      const companyPayload = {
        name: newCustomer.name.trim(), slug: newCustomer.slug.trim(),
        short_code: newCustomer.short_code.trim(),
        tax_id: newCustomer.tax_id.trim(), contact_email: newCustomer.contact_email.trim(),
        contact_phone: newCustomer.contact_phone.trim(), address: newCustomer.address.trim(),
        notes: newCustomer.notes.trim(), plan: newCustomer.plan, max_users: newCustomer.max_users,
        plan_expires_at: newCustomer.plan_expires_at ? new Date(newCustomer.plan_expires_at).toISOString() : null,
        is_active: true, updated_at: new Date().toISOString(),
      }
      const { data: company, error: companyErr } = await supabase.from('companies').insert(companyPayload).select().single()
      if (companyErr) { showToast('建立公司失敗：' + companyErr.message, 'error'); setCreating(false); return }

      const defaults = PLAN_MODULE_DEFAULTS[plan] || []
      await supabase.from('company_modules').insert(
        ALL_MODULES.map(m => ({ company_id: company.id, module: m.key, enabled: defaults.includes(m.key) }))
      )

      const { data: { session: cur } } = await supabase.auth.getSession()
      const res = await fetch(`${process.env.REACT_APP_SUPABASE_URL}/functions/v1/create-user`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${cur.access_token}` },
        body: JSON.stringify({
          accessToken: cur.access_token,
          employees: [{
            name: newCustomer.admin_name.trim(), email: newCustomer.admin_email.trim(),
            employee_id: newCustomer.admin_email.split('@')[0], password: newCustomer.admin_password,
            company_id: company.id, role: 'admin', must_change_password: true,
          }],
        }),
      })

      // 先檢查 HTTP 狀態
      if (!res.ok) {
        const errText = await res.text()
        await supabase.from('companies').delete().eq('id', company.id)
        showToast(`建立帳號失敗 (HTTP ${res.status})：${errText.slice(0, 120)}`, 'error')
        setCreating(false); return
      }

      const data = await res.json()

      // Function 層級錯誤（如 Token 無效、權限不足）
      if (data.error) {
        await supabase.from('companies').delete().eq('id', company.id)
        showToast('建立帳號失敗：' + data.error, 'error')
        setCreating(false); return
      }

      const result = data.results?.[0]
      if (!result?.success) {
        await supabase.from('companies').delete().eq('id', company.id)
        showToast('建立管理員帳號失敗：' + (result?.error || JSON.stringify(data).slice(0, 100)), 'error')
        setCreating(false); return
      }

      setCreatedResult({
        company_name: company.name, tax_id: company.tax_id || '—',
        plan: planInfo(company.plan).label,
        expires: company.plan_expires_at ? formatDate(company.plan_expires_at) : '無限制',
        admin_name: newCustomer.admin_name.trim(), admin_email: newCustomer.admin_email.trim(),
        admin_password: newCustomer.admin_password,
      })
      setShowNewCustomer(false)
      setNewCustomer(emptyNewCustomer())
      load()
    } catch (e) { showToast('發生錯誤：' + e.message, 'error') }
    setCreating(false)
  }

  const openEdit = (c) => {
    setEditingCompany(c)
    setEditForm({ ...c, plan_expires_at: c.plan_expires_at?.slice(0, 10) || '', tax_id: c.tax_id || '', notes: c.notes || '' })
  }
  const handleSaveEdit = async () => {
    if (!editForm.name || !editForm.slug) { showToast('請填公司名稱和識別碼', 'error'); return }
    setSavingEdit(true)
    const { error } = await supabase.from('companies').update({
      ...editForm,
      plan_expires_at: editForm.plan_expires_at ? new Date(editForm.plan_expires_at).toISOString() : null,
      updated_at: new Date().toISOString(),
    }).eq('id', editingCompany.id)
    if (error) { showToast('更新失敗：' + error.message, 'error') }
    else { showToast('✓ 已更新'); setEditingCompany(null); load() }
    setSavingEdit(false)
  }

  const handleToggleActive = async (id, current) => {
    await supabase.from('companies').update({ is_active: !current, updated_at: new Date().toISOString() }).eq('id', id)
    setCompanies(prev => prev.map(c => c.id === id ? { ...c, is_active: !current } : c))
    showToast(`✓ 已${!current ? '啟用' : '停用'}`)
  }

  const handleArchive = async () => {
    if (!archivingCompany) return
    await supabase.from('companies').update({
      archived: true,
      archived_at: new Date().toISOString(),
      archived_reason: archiveReason.trim(),
      is_active: false,
      updated_at: new Date().toISOString(),
    }).eq('id', archivingCompany.id)
    setCompanies(prev => prev.map(c => c.id === archivingCompany.id
      ? { ...c, archived: true, archived_at: new Date().toISOString(), archived_reason: archiveReason.trim(), is_active: false }
      : c
    ))
    showToast(`✓「${archivingCompany.name}」已封存`)
    setArchivingCompany(null)
    setArchiveReason('')
  }

  const handleUnarchive = async (id, name) => {
    await supabase.from('companies').update({
      archived: false, archived_at: null, archived_reason: '',
      is_active: true, updated_at: new Date().toISOString(),
    }).eq('id', id)
    setCompanies(prev => prev.map(c => c.id === id
      ? { ...c, archived: false, archived_at: null, archived_reason: '', is_active: true }
      : c
    ))
    showToast(`✓「${name}」已取消封存，帳號已恢復`)
  }

  const handleToggleMaintenance = async (id, current) => {
    await supabase.from('companies').update({ maintenance_mode: !current, updated_at: new Date().toISOString() }).eq('id', id)
    setCompanies(prev => prev.map(c => c.id === id ? { ...c, maintenance_mode: !current } : c))
    showToast(`✓ 維護模式已${!current ? '開啟' : '關閉'}`)
  }

  const handleExtendTrial = async (id, days) => {
    const base = companies.find(c => c.id === id)
    const from = base?.plan_expires_at && new Date(base.plan_expires_at) > new Date() ? new Date(base.plan_expires_at) : new Date()
    const newDate = new Date(from.getTime() + days * 86400000).toISOString()
    await supabase.from('companies').update({ plan_expires_at: newDate, plan: 'trial', updated_at: new Date().toISOString() }).eq('id', id)
    setCompanies(prev => prev.map(c => c.id === id ? { ...c, plan_expires_at: newDate, plan: 'trial' } : c))
    showToast(`✓ 已延長試用 ${days} 天`)
  }

  const handleUpgradePlan = async (id, plan) => {
    const expires = plan === 'enterprise' ? null : new Date(Date.now() + 365 * 86400000).toISOString()
    await supabase.from('companies').update({ plan, plan_expires_at: expires, is_active: true, updated_at: new Date().toISOString() }).eq('id', id)
    const defaults = PLAN_MODULE_DEFAULTS[plan] || []
    for (const m of ALL_MODULES) {
      await supabase.from('company_modules').upsert({ company_id: id, module: m.key, enabled: defaults.includes(m.key) }, { onConflict: 'company_id,module' })
    }
    setCompanies(prev => prev.map(c => c.id === id ? { ...c, plan, plan_expires_at: expires } : c))
    const newMods = {}; ALL_MODULES.forEach(m => { newMods[m.key] = defaults.includes(m.key) })
    setCompanyModules(prev => ({ ...prev, [id]: newMods }))
    showToast(`✓ 已切換為「${planInfo(plan).label}」`)
  }

  const handleToggleCompanyModule = async (companyId, module, current) => {
    await supabase.from('company_modules').upsert({ company_id: companyId, module, enabled: !current }, { onConflict: 'company_id,module' })
    setCompanyModules(prev => ({ ...prev, [companyId]: { ...prev[companyId], [module]: !current } }))
  }

  const handleApplyPlanDefaults = async (companyId, plan) => {
    setSavingModules(true)
    const defaults = PLAN_MODULE_DEFAULTS[plan] || []
    for (const m of ALL_MODULES) {
      await supabase.from('company_modules').upsert({ company_id: companyId, module: m.key, enabled: defaults.includes(m.key) }, { onConflict: 'company_id,module' })
    }
    const newMods = {}; ALL_MODULES.forEach(m => { newMods[m.key] = defaults.includes(m.key) })
    setCompanyModules(prev => ({ ...prev, [companyId]: newMods }))
    setSavingModules(false)
    showToast('✓ 已套用方案預設')
  }

  const handleSetAdmin = async (userId) => {
    await supabase.from('user_profiles').update({ role: 'admin' }).eq('id', userId)
    setAllUsers(prev => prev.map(u => u.id === userId ? { ...u, role: 'admin' } : u))
    showToast('✓ 已設定為公司管理員')
    if (selectedCompany) loadCompanyDetail(selectedCompany)
  }

  // ── 刪除客戶（只允許封存狀態）── 透過 Edge Function（service role 繞過 RLS）
  const handleDeleteCompany = async () => {
    if (deleteConfirmName !== deletingCompany.name) { showToast('公司名稱輸入不正確', 'error'); return }
    setDeleting(true)
    try {
      const { data: { session } } = await supabase.auth.getSession()
      const res = await fetch(`${process.env.REACT_APP_SUPABASE_URL}/functions/v1/delete-company`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${session.access_token}` },
        body: JSON.stringify({ companyId: deletingCompany.id }),
      })
      const data = await res.json()
      if (data.error) {
        showToast('刪除失敗：' + data.error, 'error')
        setDeleting(false); return
      }
      showToast(`🗑 已永久刪除「${deletingCompany.name}」（含 ${data.members} 位員工帳號）`)
      setDeletingCompany(null); setDeleteConfirmName(''); setDeleting(false)
      load()
    } catch (e) {
      showToast('刪除失敗：' + e.message, 'error')
      setDeleting(false)
    }
  }

  // ── 廣播通知 ──
  const handleBroadcast = async () => {
    if (!broadcastForm.title.trim() || !broadcastForm.body.trim()) { showToast('請填寫標題和內容', 'error'); return }
    setBroadcasting(true)
    let targets = allUsers.filter(u => !u.is_super_admin && u.approved !== false)
    if (broadcastForm.target === 'company' && broadcastForm.company_id) {
      targets = targets.filter(u => u.company_id === broadcastForm.company_id)
    }
    if (!targets.length) { showToast('沒有符合的用戶', 'error'); setBroadcasting(false); return }

    // 分批插入（Supabase 每次最多約 500 筆）
    const BATCH = 200
    let hasError = false
    for (let i = 0; i < targets.length; i += BATCH) {
      const batch = targets.slice(i, i + BATCH)
      const { error } = await supabase.from('notifications').insert(
        batch.map(u => ({
          user_id: u.id,
          title:   broadcastForm.title.trim(),
          body:    broadcastForm.body.trim(),
          is_read: false,
          url:     '/',
          source:  '系統通知',
        }))
      )
      if (error) { hasError = true; console.error('broadcast insert error:', error); break }
    }

    setBroadcasting(false)
    if (hasError) {
      showToast('發送失敗！可能原因：資料庫 RLS 政策不允許跨用戶寫入通知。請在 Supabase → Table Editor → notifications → Policies 確認有「允許任何已登入用戶插入」的政策。', 'error')
      return
    }
    showToast(`📢 已成功發送通知給 ${targets.length} 位用戶`)
    setBroadcastForm({ target: 'all', company_id: '', title: '', body: '' })
  }

  // ── 維護排程 ──
  const handleCreateSchedule = async () => {
    if (!scheduleForm.title.trim()) { showToast('請填寫標題', 'error'); return }
    if (!scheduleForm.starts_at || !scheduleForm.ends_at) { showToast('請填寫維護時段', 'error'); return }
    if (new Date(scheduleForm.ends_at) <= new Date(scheduleForm.starts_at)) { showToast('結束時間必須晚於開始時間', 'error'); return }
    setSavingSchedule(true)
    const { data, error } = await supabase.from('maintenance_schedules').insert({
      company_id: scheduleForm.company_id || null,
      title: scheduleForm.title.trim(),
      body: scheduleForm.body.trim(),
      starts_at: new Date(scheduleForm.starts_at).toISOString(),
      ends_at: new Date(scheduleForm.ends_at).toISOString(),
      contact_name: scheduleForm.contact_name.trim(),
      contact_phone: scheduleForm.contact_phone.trim(),
      contact_email: scheduleForm.contact_email.trim(),
      status: 'scheduled',
    }).select().single()
    setSavingSchedule(false)
    if (error) { showToast('建立失敗：' + error.message, 'error'); return }
    setSchedules(prev => [data, ...prev])
    setShowScheduleForm(false)
    setScheduleForm(emptySchedule())
    showToast('✓ 維護排程已建立，可點「發送通知」通知客戶')
  }

  const handleSendMaintenanceNotify = async (sched) => {
    const company = companies.find(c => c.id === sched.company_id)
    const companyName = company?.name || '全部客戶'
    let targets = allUsers.filter(u => !u.is_super_admin && u.approved !== false)
    if (sched.company_id) targets = targets.filter(u => u.company_id === sched.company_id)
    if (!targets.length) { showToast('沒有符合的用戶', 'error'); return }

    const fmt = iso => new Date(iso).toLocaleString('zh-TW', { month:'numeric', day:'numeric', hour:'2-digit', minute:'2-digit' })
    const contactParts = []
    if (sched.contact_name) contactParts.push(sched.contact_name)
    if (sched.contact_phone) contactParts.push(sched.contact_phone)
    if (sched.contact_email) contactParts.push(sched.contact_email)
    const contactStr = contactParts.length ? `\n如需更改維護時間請聯繫：${contactParts.join(' / ')}` : ''

    const BATCH = 200
    let hasError = false
    for (let i = 0; i < targets.length; i += BATCH) {
      const batch = targets.slice(i, i + BATCH)
      const { error } = await supabase.from('notifications').insert(
        batch.map(u => ({
          user_id: u.id,
          title: `🔧 ${sched.title}`,
          body: `${sched.body ? sched.body + '\n' : ''}維護時段：${fmt(sched.starts_at)} ～ ${fmt(sched.ends_at)}${contactStr}`,
          is_read: false,
          source: '系統通知',
        }))
      )
      if (error) { hasError = true; break }
    }
    if (hasError) { showToast('通知發送失敗', 'error'); return }
    await supabase.from('maintenance_schedules').update({ notify_sent: true }).eq('id', sched.id)
    setSchedules(prev => prev.map(s => s.id === sched.id ? { ...s, notify_sent: true } : s))
    showToast(`📢 已通知 ${targets.length} 位用戶（${companyName}）`)
  }

  const handleSaveScheduleEdit = async () => {
    if (!scheduleForm.title.trim()) { showToast('請填寫標題', 'error'); return }
    if (!scheduleForm.starts_at || !scheduleForm.ends_at) { showToast('請填寫維護時段', 'error'); return }
    if (new Date(scheduleForm.ends_at) <= new Date(scheduleForm.starts_at)) { showToast('結束時間必須晚於開始時間', 'error'); return }
    setSavingSchedule(true)
    const updates = {
      company_id:    scheduleForm.company_id || null,
      title:         scheduleForm.title.trim(),
      body:          scheduleForm.body.trim(),
      starts_at:     new Date(scheduleForm.starts_at).toISOString(),
      ends_at:       new Date(scheduleForm.ends_at).toISOString(),
      contact_name:  scheduleForm.contact_name.trim(),
      contact_phone: scheduleForm.contact_phone.trim(),
      contact_email: scheduleForm.contact_email.trim(),
      // 若有重新通知則重置 notify_sent，讓發送紀錄更新
      ...(scheduleRenotify ? { notify_sent: false } : {}),
    }
    const { error } = await supabase.from('maintenance_schedules').update(updates).eq('id', editingSchedule.id)
    if (error) { showToast('更新失敗：' + error.message, 'error'); setSavingSchedule(false); return }
    const updated = { ...editingSchedule, ...updates }
    setSchedules(prev => prev.map(s => s.id === editingSchedule.id ? updated : s))
    // 重新通知
    if (scheduleRenotify) {
      const fmt = iso => new Date(iso).toLocaleString('zh-TW', { month:'numeric', day:'numeric', hour:'2-digit', minute:'2-digit' })
      const contactParts = []
      if (scheduleForm.contact_name) contactParts.push(scheduleForm.contact_name)
      if (scheduleForm.contact_phone) contactParts.push(scheduleForm.contact_phone)
      if (scheduleForm.contact_email) contactParts.push(scheduleForm.contact_email)
      const contactStr = contactParts.length ? `\n如需協調請聯繫：${contactParts.join(' / ')}` : ''
      let targets = allUsers.filter(u => !u.is_super_admin && u.approved !== false)
      if (scheduleForm.company_id) targets = targets.filter(u => u.company_id === scheduleForm.company_id)
      const BATCH = 200
      let hasError = false
      for (let i = 0; i < targets.length; i += BATCH) {
        const batch = targets.slice(i, i + BATCH)
        const { error: ne } = await supabase.from('notifications').insert(
          batch.map(u => ({
            user_id: u.id,
            title:   `🔔 維護時間變更：${scheduleForm.title.trim()}`,
            body:    `維護時段已更新為：${fmt(updates.starts_at)} ～ ${fmt(updates.ends_at)}${scheduleForm.body ? '\n' + scheduleForm.body : ''}${contactStr}`,
            is_read: false,
            source:  '系統通知',
          }))
        )
        if (ne) { hasError = true; break }
      }
      await supabase.from('maintenance_schedules').update({ notify_sent: true }).eq('id', editingSchedule.id)
      setSchedules(prev => prev.map(s => s.id === editingSchedule.id ? { ...s, ...updates, notify_sent: true } : s))
      if (!hasError) showToast(`✓ 已更新排程並重新通知 ${targets.length} 位用戶`)
      else showToast('排程已更新，但通知發送失敗', 'error')
    } else {
      showToast('✓ 排程已更新')
    }
    setSavingSchedule(false)
    setEditingSchedule(null)
    setScheduleForm(emptySchedule())
  }

  const handleCompleteSchedule = async (id) => {
    if (!window.confirm('確定標記此維護排程為已完成？\n\n標記後客戶端的維護橫幅將會消失。')) return
    await supabase.from('maintenance_schedules').update({ status: 'completed' }).eq('id', id)
    setSchedules(prev => prev.map(s => s.id === id ? { ...s, status: 'completed' } : s))
    showToast('✓ 已標記為完成')
  }

  const handleCancelSchedule = async (id) => {
    await supabase.from('maintenance_schedules').update({ status: 'cancelled' }).eq('id', id)
    setSchedules(prev => prev.map(s => s.id === id ? { ...s, status: 'cancelled' } : s))
    showToast('✓ 已取消排程')
  }

  const handleDeleteSchedule = async (id) => {
    if (!window.confirm('確定刪除此維護排程？')) return
    await supabase.from('maintenance_schedules').delete().eq('id', id)
    setSchedules(prev => prev.filter(s => s.id !== id))
    showToast('✓ 已刪除')
  }

  const schedStatus = (s) => {
    if (s.status === 'cancelled') return { label: '已取消', color: '#94a3b8', bg: '#f1f5f9' }
    if (s.status === 'completed') return { label: '已完成', color: '#15803d', bg: '#dcfce7' }
    const now = new Date(), start = new Date(s.starts_at)
    if (now < start) return { label: '待執行', color: '#2563eb', bg: '#eff6ff' }
    // 時間到自動視為「維護中」，但不會自動結束 — 需手動標記完成（方便延長維護時間）
    return { label: '維護中', color: '#d97706', bg: '#fef3c7' }
  }

  // ── 代理登入 ──
  const handleImpersonate = async (userId, userName) => {
    if (!window.confirm(`以「${userName}」身份代理登入？\n\n系統將產生一次性登入連結，在新分頁開啟。`)) return
    setImpersonating(true)
    try {
      const { data: { session } } = await supabase.auth.getSession()
      const res = await fetch(`${process.env.REACT_APP_SUPABASE_URL}/functions/v1/impersonate-user`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${session.access_token}` },
        body: JSON.stringify({ accessToken: session.access_token, userId }),
      })
      const data = await res.json()
      if (data.error) { showToast('代理登入失敗：' + data.error, 'error'); setImpersonating(false); return }
      showToast(`🔑 代理登入：${data.name}，新分頁已開啟`)
      window.open(data.link, '_blank')
    } catch (e) { showToast('代理登入失敗：' + e.message, 'error') }
    setImpersonating(false)
  }

  const activeCompanies = companies.filter(c => !c.archived)
  const archivedCompanies = companies.filter(c => c.archived)
  const paidCompanies = activeCompanies.filter(c => ['starter', 'professional', 'enterprise'].includes(c.plan)).length
  const trialCompanies = activeCompanies.filter(c => c.plan === 'trial').length
  const expiringSoon = activeCompanies.filter(c => {
    if (!c.plan_expires_at) return false
    const d = (new Date(c.plan_expires_at) - new Date()) / 86400000
    return d > 0 && d <= 7
  })
  const displayedCompanies = showArchived ? archivedCompanies : activeCompanies
  const pagedCompanies = displayedCompanies.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE)

  const NAV = [
    { key: 'companies', icon: '🏢', label: '客戶管理', en: 'Customers', badge: companies.length },
    { key: 'modules',   icon: '🧩', label: '功能模組', en: 'Modules' },
    { key: 'schedule',  icon: '🗓', label: '維護排程', en: 'Maintenance', badge: schedules.filter(s => schedStatus(s).label === '待執行').length || undefined },
    { key: 'broadcast', icon: '📢', label: '廣播通知', en: 'Broadcast' },
    { key: 'users',     icon: '👥', label: '所有用戶', en: 'All Users', badge: allUsers.length },
    { key: 'logs',      icon: '📋', label: '操作日誌', en: 'Logs' },
  ]

  const SIDEBAR_W = 220

  return (
    <div style={{ display: 'flex', minHeight: '100vh', fontFamily: "'Noto Sans TC','PingFang TC',sans-serif", background: '#0f172a' }}>

      {/* ── Sidebar ── */}
      <div style={{ width: SIDEBAR_W, flexShrink: 0, background: 'linear-gradient(180deg, #0a1628 0%, #0f2044 100%)', display: 'flex', flexDirection: 'column', position: 'fixed', top: 0, left: 0, height: '100vh', zIndex: 100, borderRight: '1px solid rgba(56,189,248,0.15)' }}>

        {/* Brand */}
        <div style={{ padding: '22px 20px 16px', borderBottom: '1px solid rgba(56,189,248,0.12)' }}>
          <div style={{ fontSize: 11, color: '#38bdf8', fontWeight: 800, letterSpacing: 2.5, textTransform: 'uppercase', marginBottom: 4 }}>YPFOPS</div>
          <div style={{ fontSize: 15, fontWeight: 900, color: '#fff', lineHeight: 1.2 }}>管理後台</div>
          <div style={{ fontSize: 10, color: 'rgba(148,163,184,0.7)', marginTop: 3 }}>Super Admin Portal</div>
        </div>

        {/* Admin info */}
        <div style={{ padding: '14px 16px', borderBottom: '1px solid rgba(56,189,248,0.08)', display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{ width: 36, height: 36, borderRadius: '50%', background: 'linear-gradient(135deg,#f59e0b,#d97706)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 15, fontWeight: 900, color: '#fff', flexShrink: 0 }}>
            {(userProfile?.name || 'A')[0].toUpperCase()}
          </div>
          <div style={{ overflow: 'hidden' }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: '#fff', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{userProfile?.name}</div>
            <div style={{ fontSize: 10, color: '#fbbf24', marginTop: 1 }}>👑 Super Admin</div>
          </div>
        </div>

        {/* 新增客戶 CTA */}
        <div style={{ padding: '12px 14px 4px' }}>
          <button
            style={{ width: '100%', padding: '12px 14px', borderRadius: 10, background: 'linear-gradient(135deg,#16a34a,#15803d)', border: 'none', color: '#fff', fontSize: 14, fontWeight: 800, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, boxShadow: '0 2px 12px rgba(22,163,74,0.4)' }}
            onClick={() => { setShowNewCustomer(true); setNewCustomer(emptyNewCustomer()) }}>
            <span style={{ fontSize: 18 }}>＋</span> 新增客戶
          </button>
        </div>

        {/* Nav */}
        <div style={{ flex: 1, padding: '8px 10px', overflowY: 'auto' }}>
          {NAV.map(item => {
            const active = tab === item.key
            return (
              <div key={item.key} onClick={() => setTab(item.key)}
                style={{ padding: '12px 14px', borderRadius: 10, marginBottom: 4, cursor: 'pointer', background: active ? 'rgba(56,189,248,0.18)' : 'transparent', border: active ? '1px solid rgba(56,189,248,0.35)' : '1px solid transparent', display: 'flex', alignItems: 'center', gap: 12, transition: 'all 0.15s' }}>
                <span style={{ fontSize: 18, flexShrink: 0 }}>{item.icon}</span>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 14, fontWeight: active ? 700 : 500, color: active ? '#e0f2fe' : 'rgba(148,163,184,0.85)' }}>{item.label}</div>
                  <div style={{ fontSize: 10, color: 'rgba(100,116,139,0.8)' }}>{item.en}</div>
                </div>
                {item.badge !== undefined && (
                  <span style={{ background: active ? 'rgba(56,189,248,0.3)' : 'rgba(255,255,255,0.08)', color: active ? '#38bdf8' : '#94a3b8', borderRadius: 20, padding: '2px 8px', fontSize: 11, fontWeight: 700 }}>{item.badge}</span>
                )}
              </div>
            )
          })}
        </div>

        {/* Stats mini */}
        <div style={{ padding: '10px 14px', borderTop: '1px solid rgba(56,189,248,0.1)', borderBottom: '1px solid rgba(56,189,248,0.1)' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 6 }}>
            {[
              { label: '客戶', value: activeCompanies.length, color: '#38bdf8' },
              { label: '付費', value: paidCompanies, color: '#22c55e' },
              { label: '試用', value: trialCompanies, color: '#fbbf24' },
              { label: '到期', value: expiringSoon.length, color: expiringSoon.length > 0 ? '#f87171' : '#475569' },
              { label: '封存', value: archivedCompanies.length, color: archivedCompanies.length > 0 ? '#94a3b8' : '#475569' },
            ].map(s => (
              <div key={s.label} style={{ textAlign: 'center', padding: '8px 6px', background: 'rgba(255,255,255,0.04)', borderRadius: 8 }}>
                <div style={{ fontSize: 20, fontWeight: 900, color: s.color }}>{s.value}</div>
                <div style={{ fontSize: 10, color: '#64748b' }}>{s.label}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Logout */}
        <div style={{ padding: '12px 14px' }}>
          <button onClick={onLogout} style={{ width: '100%', padding: '11px', borderRadius: 10, background: 'rgba(239,68,68,0.12)', border: '1.5px solid rgba(239,68,68,0.3)', color: '#fca5a5', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>
            ⏏ 登出
          </button>
        </div>
      </div>

      {/* ── Main Content ── */}
      <div style={{ marginLeft: SIDEBAR_W, flex: 1, minHeight: '100vh', background: '#f1f5f9', overflowY: 'auto' }}>

        {/* Top bar */}
        <div style={{ background: '#fff', borderBottom: '1.5px solid #e2e8f0', padding: '16px 28px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', position: 'sticky', top: 0, zIndex: 50, boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}>
          <div>
            <div style={{ fontSize: 20, fontWeight: 900, color: '#0f172a' }}>
              {NAV.find(n => n.key === tab)?.icon} {NAV.find(n => n.key === tab)?.label}
            </div>
            <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>{NAV.find(n => n.key === tab)?.en}</div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            {expiringSoon.length > 0 && (
              <div style={{ padding: '8px 14px', background: '#fef3c7', border: '1.5px solid #f59e0b', borderRadius: 10, fontSize: 12, fontWeight: 700, color: '#92400e' }}>
                ⚠️ {expiringSoon.length} 家即將到期
              </div>
            )}
            <button style={{ padding: '8px 14px', borderRadius: 10, background: '#f1f5f9', border: '1.5px solid #e2e8f0', color: '#475569', fontSize: 13, fontWeight: 600, cursor: 'pointer' }} onClick={load}>
              ↻ 重新整理
            </button>
          </div>
        </div>

        <div style={{ padding: '24px 28px' }}>

          {/* 建立成功摘要卡 */}
          {createdResult && (
            <div style={{ background: '#f0fdf4', border: '2px solid #86efac', borderRadius: 14, padding: '20px 24px', marginBottom: 20 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16, flexWrap: 'wrap' }}>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 16, fontWeight: 800, color: '#15803d', marginBottom: 14 }}>✅ 客戶建立成功！請將以下資訊提供給客戶</div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(190px, 1fr))', gap: 10 }}>
                    {[
                      ['公司名稱', createdResult.company_name],
                      ['統一編號', createdResult.tax_id],
                      ['訂閱方案', createdResult.plan],
                      ['到期日', createdResult.expires],
                      ['管理員姓名', createdResult.admin_name],
                      ['登入 Email', createdResult.admin_email],
                    ].map(([label, value]) => (
                      <div key={label} style={{ padding: '10px 14px', background: '#fff', borderRadius: 10, border: '1.5px solid #bbf7d0' }}>
                        <div style={{ fontSize: 11, color: '#64748b', marginBottom: 3, fontWeight: 600 }}>{label}</div>
                        <div style={{ fontSize: 13, fontWeight: 700, color: '#0f172a' }}>{value}</div>
                      </div>
                    ))}
                    <div style={{ padding: '10px 14px', background: '#fff', borderRadius: 10, border: '2px solid #3b82f6', gridColumn: 'span 2' }}>
                      <div style={{ fontSize: 11, color: '#64748b', marginBottom: 3, fontWeight: 600 }}>預設密碼（首次登入需更改）</div>
                      <div style={{ fontSize: 20, fontWeight: 900, color: '#1d4ed8', fontFamily: 'monospace', letterSpacing: 3 }}>{createdResult.admin_password}</div>
                    </div>
                  </div>
                </div>
                <button style={{ padding: '8px 18px', borderRadius: 8, background: '#fff', border: '1.5px solid #86efac', color: '#15803d', fontSize: 13, fontWeight: 700, cursor: 'pointer', flexShrink: 0 }} onClick={() => setCreatedResult(null)}>關閉</button>
              </div>
            </div>
          )}

          {/* ── 客戶管理 ── */}
          {tab === 'companies' && (
            <div>
              {/* 切換封存檢視 */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14, flexWrap: 'wrap' }}>
                <button
                  onClick={() => { setShowArchived(false); setPage(1) }}
                  style={{ padding: '8px 18px', borderRadius: 10, border: `2px solid ${!showArchived ? '#3b82f6' : '#e2e8f0'}`, background: !showArchived ? '#eff6ff' : '#fff', color: !showArchived ? '#1d4ed8' : '#64748b', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>
                  🏢 使用中客戶 <span style={{ marginLeft: 6, background: !showArchived ? '#3b82f6' : '#e2e8f0', color: !showArchived ? '#fff' : '#94a3b8', borderRadius: 20, padding: '1px 8px', fontSize: 12 }}>{activeCompanies.length}</span>
                </button>
                <button
                  onClick={() => { setShowArchived(true); setPage(1) }}
                  style={{ padding: '8px 18px', borderRadius: 10, border: `2px solid ${showArchived ? '#64748b' : '#e2e8f0'}`, background: showArchived ? '#f1f5f9' : '#fff', color: showArchived ? '#334155' : '#94a3b8', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>
                  🗄️ 封存客戶 <span style={{ marginLeft: 6, background: showArchived ? '#64748b' : '#e2e8f0', color: showArchived ? '#fff' : '#94a3b8', borderRadius: 20, padding: '1px 8px', fontSize: 12 }}>{archivedCompanies.length}</span>
                </button>
                {showArchived && (
                  <div style={{ fontSize: 13, color: '#64748b', padding: '8px 14px', background: '#f8fafc', borderRadius: 10, border: '1.5px solid #e2e8f0' }}>
                    🗄️ 顯示封存客戶 — 資料保留，帳號已停用
                  </div>
                )}
              </div>

              <div style={{ background: '#fff', borderRadius: 14, border: `1.5px solid ${showArchived ? '#cbd5e1' : '#e2e8f0'}`, overflow: 'hidden', boxShadow: '0 1px 4px rgba(0,0,0,0.05)' }}>
                <ScrollTable>
                  <thead>
                    <tr style={{ background: showArchived ? '#f8fafc' : '#f8fafc' }}>
                      {(showArchived
                        ? ['公司名稱', '統編', '封存原因', '封存日期', '用戶數', '操作']
                        : ['公司名稱', '統編', '方案', '到期日', '用戶數', '狀態', '操作']
                      ).map(h => (
                        <th key={h} style={{ ...st.th, background: 'transparent', color: '#475569' }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {pagedCompanies.length === 0 ? (
                      <tr><td colSpan={7}>
                        <EmptyState icon={showArchived ? '🗄️' : '🏢'} text={showArchived ? '沒有封存的客戶' : '尚無客戶，點左側「新增客戶」開始'} />
                      </td></tr>
                    ) : pagedCompanies.map(c => {
                      const pi = planInfo(c.plan)
                      const userCount = allUsers.filter(u => u.company_id === c.id).length
                      const isExpired = c.plan_expires_at && new Date(c.plan_expires_at) < new Date()
                      const daysLeft = c.plan_expires_at ? Math.ceil((new Date(c.plan_expires_at) - new Date()) / 86400000) : null
                      return (
                        <tr key={c.id} style={{ borderBottom: '1px solid #f1f5f9', opacity: showArchived ? 0.75 : 1 }}>
                          <td style={{ ...st.td, fontWeight: 700, color: showArchived ? '#64748b' : '#0f172a' }}>
                            {showArchived && <span style={{ fontSize: 12, marginRight: 6 }}>🗄️</span>}
                            {c.name}
                            {c.maintenance_mode && !showArchived && <span style={{ marginLeft: 6, fontSize: 10, background: '#fde68a', color: '#92400e', padding: '2px 7px', borderRadius: 8, fontWeight: 700 }}>🔧維護</span>}
                            {c.notes && <div style={{ fontSize: 11, color: '#94a3b8', maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.notes}</div>}
                          </td>
                          <td style={{ ...st.td, fontFamily: 'monospace', fontSize: 12, color: '#64748b' }}>{c.tax_id || '—'}</td>

                          {showArchived ? (
                            <>
                              <td style={{ ...st.td, fontSize: 13, color: '#475569', maxWidth: 200 }}>{c.archived_reason || <span style={{ color: '#cbd5e1' }}>—</span>}</td>
                              <td style={{ ...st.td, fontSize: 12, color: '#94a3b8' }}>{c.archived_at ? formatDate(c.archived_at) : '—'}</td>
                            </>
                          ) : (
                            <>
                              <td style={st.td}><span style={{ ...st.badge('ok'), background: pi.bg, color: pi.color, border: `1.5px solid ${pi.color}44` }}>{pi.label}</span></td>
                              <td style={{ ...st.td, color: isExpired ? C.red : daysLeft !== null && daysLeft <= 7 ? '#d97706' : '#64748b', fontWeight: (isExpired || (daysLeft !== null && daysLeft <= 7)) ? 700 : 400 }}>
                                {c.plan_expires_at ? formatDate(c.plan_expires_at) : '無限制'}
                                {isExpired && <div style={{ fontSize: 10, color: C.red }}>已到期</div>}
                                {!isExpired && daysLeft !== null && daysLeft <= 7 && <div style={{ fontSize: 10, color: '#d97706' }}>剩 {daysLeft} 天</div>}
                              </td>
                            </>
                          )}

                          <td style={{ ...st.td, color: userCount >= c.max_users ? C.red : '#0f172a', fontWeight: userCount >= c.max_users ? 700 : 400 }}>{userCount} / {c.max_users}</td>

                          {!showArchived && (
                            <td style={st.td}>
                              {c.maintenance_mode
                                ? <span style={{ ...st.badge(''), background: '#fde68a', color: '#92400e', border: '1.5px solid #f59e0b' }}>🔧 維護中</span>
                                : <span style={{ ...st.badge(c.is_active ? 'ok' : ''), fontSize: 12 }}>{c.is_active ? '✅ 正常' : '🔴 停用'}</span>}
                            </td>
                          )}

                          <td style={st.td}>
                            <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap' }}>
                              <button style={{ ...st.btn('default'), padding: '5px 10px', fontSize: 12, minHeight: 32 }} onClick={() => loadCompanyDetail(c)}>詳情</button>
                              {showArchived ? (
                                <>
                                  <button style={{ ...st.btn('success'), padding: '5px 10px', fontSize: 12, minHeight: 32 }} onClick={() => handleUnarchive(c.id, c.name)}>
                                    ↩ 取消封存
                                  </button>
                                  <button style={{ ...st.btn('danger'), padding: '5px 10px', fontSize: 12, minHeight: 32 }}
                                    onClick={() => { setDeletingCompany({ id: c.id, name: c.name }); setDeleteConfirmName('') }}>
                                    🗑 永久刪除
                                  </button>
                                </>
                              ) : (
                                <>
                                  <button style={{ ...st.btn('warning'), padding: '5px 10px', fontSize: 12, minHeight: 32 }} onClick={() => openEdit(c)}>編輯</button>
                                  {c.plan === 'trial' && (
                                    <button style={{ ...st.btn('primary'), padding: '5px 10px', fontSize: 12, minHeight: 32 }} onClick={() => handleExtendTrial(c.id, 14)}>+14天</button>
                                  )}
                                  <button style={{ ...st.btn(c.maintenance_mode ? 'success' : 'warning'), padding: '5px 10px', fontSize: 12, minHeight: 32 }} onClick={() => handleToggleMaintenance(c.id, c.maintenance_mode)}>
                                    {c.maintenance_mode ? '解除維護' : '🔧維護'}
                                  </button>
                                  <button style={{ ...st.btn(c.is_active ? 'danger' : 'success'), padding: '5px 10px', fontSize: 12, minHeight: 32 }} onClick={() => handleToggleActive(c.id, c.is_active)}>
                                    {c.is_active ? '停用' : '啟用'}
                                  </button>
                                  <button style={{ padding: '5px 10px', fontSize: 12, minHeight: 32, borderRadius: 8, border: '1.5px solid #cbd5e1', background: '#f8fafc', color: '#475569', cursor: 'pointer', fontWeight: 600 }}
                                    onClick={() => { setArchivingCompany({ id: c.id, name: c.name }); setArchiveReason('') }}>
                                    🗄️ 封存
                                  </button>
                                </>
                              )}
                            </div>
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </ScrollTable>
                <div style={{ padding: '0 8px' }}>
                  <Pagination page={page} total={Math.ceil(displayedCompanies.length / PAGE_SIZE)} onChange={setPage} />
                </div>
              </div>
            </div>
          )}

          {/* ── 功能模組 ── */}
          {tab === 'modules' && (
            <div>
              {companies.length === 0 ? (
                <div style={{ background: '#fff', borderRadius: 14, border: '1.5px solid #e2e8f0', padding: 24 }}>
                  <EmptyState icon="🧩" text="尚無客戶公司" />
                </div>
              ) : companies.map(c => {
                const mods = companyModules[c.id] || {}
                const pi = planInfo(c.plan)
                const isOpen = moduleTab === c.id
                const enabledCount = ALL_MODULES.filter(m => mods[m.key]).length
                return (
                  <div key={c.id} style={{ background: '#fff', borderRadius: 14, marginBottom: 12, border: isOpen ? `2px solid ${C.accent}` : '1.5px solid #e2e8f0', overflow: 'hidden', boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12, cursor: 'pointer', padding: '16px 20px' }} onClick={() => setModuleTab(isOpen ? null : c.id)}>
                      <div style={{ flex: 1 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                          <span style={{ fontSize: 15, fontWeight: 800, color: '#0f172a' }}>{c.name}</span>
                          <span style={{ ...st.badge('ok'), background: pi.bg, color: pi.color, fontSize: 11 }}>{pi.label}</span>
                        </div>
                        <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>已啟用 {enabledCount}/{ALL_MODULES.length} 個模組</div>
                      </div>
                      <div style={{ display: 'flex', gap: 3, flexWrap: 'wrap', maxWidth: 240, justifyContent: 'flex-end' }}>
                        {ALL_MODULES.filter(m => mods[m.key]).map(m => (
                          <span key={m.key} style={{ fontSize: 12, padding: '2px 7px', background: '#eff6ff', color: '#3b82f6', borderRadius: 6, fontWeight: 600 }}>{m.icon}</span>
                        ))}
                      </div>
                      <span style={{ fontSize: 18, color: '#94a3b8', marginLeft: 8 }}>{isOpen ? '▲' : '▼'}</span>
                    </div>
                    {isOpen && (
                      <div style={{ padding: '0 20px 20px', borderTop: '1.5px solid #f1f5f9' }}>
                        <div style={{ marginTop: 16, marginBottom: 12 }}>
                          <div style={{ fontSize: 12, color: '#64748b', marginBottom: 8, fontWeight: 600 }}>快速套用方案預設：</div>
                          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                            {PLANS.filter(p => p.key !== 'suspended').map(p => (
                              <button key={p.key} style={{ ...st.btn('default'), padding: '6px 14px', fontSize: 12, minHeight: 32, background: p.bg, color: p.color, borderColor: p.color + '44' }}
                                onClick={() => handleApplyPlanDefaults(c.id, p.key)} disabled={savingModules}>{p.label}</button>
                            ))}
                          </div>
                        </div>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(155px, 1fr))', gap: 10 }}>
                          {ALL_MODULES.map(m => {
                            const enabled = !!mods[m.key]
                            return (
                              <label key={m.key} onClick={() => handleToggleCompanyModule(c.id, m.key, enabled)}
                                style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', borderRadius: 10, cursor: 'pointer',
                                  background: enabled ? '#f0fdf4' : '#f8fafc', border: `2px solid ${enabled ? '#86efac' : '#e2e8f0'}`, transition: 'all 0.15s' }}>
                                <span style={{ fontSize: 18 }}>{m.icon}</span>
                                <div style={{ flex: 1 }}>
                                  <div style={{ fontSize: 13, fontWeight: 700, color: enabled ? '#15803d' : '#94a3b8' }}>{m.label}</div>
                                  <div style={{ fontSize: 10, color: enabled ? '#16a34a' : '#cbd5e1' }}>{enabled ? '✅ 啟用' : '— 停用'}</div>
                                </div>
                              </label>
                            )
                          })}
                        </div>
                        <div style={{ marginTop: 14, paddingTop: 12, borderTop: '1px solid #f1f5f9', display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                          <span style={{ fontSize: 12, color: '#64748b', fontWeight: 600 }}>升級方案：</span>
                          {PLANS.filter(p => p.key !== 'suspended').map(p => (
                            <button key={p.key} style={{ ...st.btn(c.plan === p.key ? 'primary' : 'default'), padding: '6px 14px', fontSize: 12, minHeight: 32 }}
                              onClick={() => handleUpgradePlan(c.id, p.key)} disabled={c.plan === p.key}>
                              {c.plan === p.key ? `✓ ${p.label}` : p.label}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )
              })}
            </div>
          )}

          {/* ── 所有用戶（依公司分組）── */}
          {tab === 'users' && (() => {
            const unassigned = allUsers.filter(u => !u.company_id && !u.is_super_admin)
            const superAdmins = allUsers.filter(u => u.is_super_admin)

            const UserRow = ({ u }) => (
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 16px', background: '#f8fafc', borderRadius: 10, marginBottom: 8, gap: 10, flexWrap: 'wrap', border: '1.5px solid #f1f5f9' }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 14, fontWeight: 700, color: '#0f172a', display: 'flex', alignItems: 'center', gap: 7, flexWrap: 'wrap' }}>
                    {u.name}
                    {u.role === 'admin'
                      ? <span style={{ ...st.badge('ok'), background: '#eff6ff', color: '#3b82f6', fontSize: 10 }}>⚙️ 管理員</span>
                      : u.role === 'manager'
                        ? <span style={{ ...st.badge('ok'), background: '#fdf4ff', color: '#7c3aed', fontSize: 10 }}>🏅 主管</span>
                        : <span style={{ ...st.badge(''), fontSize: 10 }}>👤 員工</span>}
                    {!u.approved && <span style={{ ...st.badge(''), background: '#fef9c3', color: '#92400e', fontSize: 10 }}>⏳ 待審</span>}
                  </div>
                  <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>{u.email}{u.position ? ` · ${u.position}` : ''}{u.employee_id ? ` · #${u.employee_id}` : ''}</div>
                </div>
                <div style={{ display: 'flex', gap: 6, flexShrink: 0, flexWrap: 'wrap', alignItems: 'center' }}>
                  {u.role !== 'admin' && (
                    <button style={{ ...st.btn('primary'), padding: '5px 10px', fontSize: 11, minHeight: 30 }} onClick={() => handleSetAdmin(u.id)}>設管理員</button>
                  )}
                  <button style={{ padding: '5px 10px', fontSize: 11, minHeight: 30, borderRadius: 8, border: '1.5px solid #38bdf844', background: '#0f172a', color: '#38bdf8', cursor: 'pointer', fontWeight: 600 }}
                    onClick={() => handleImpersonate(u.id, u.name)} disabled={impersonating}>
                    🔑 代理
                  </button>
                </div>
              </div>
            )

            return (
              <div>
                {/* 統計列 */}
                <div style={{ display: 'flex', gap: 10, marginBottom: 18, flexWrap: 'wrap' }}>
                  {[
                    { label: '全部用戶', value: allUsers.filter(u => !u.is_super_admin).length, color: '#3b82f6', bg: '#eff6ff' },
                    { label: '管理員', value: allUsers.filter(u => u.role === 'admin' && !u.is_super_admin).length, color: '#7c3aed', bg: '#fdf4ff' },
                    { label: '未指派', value: unassigned.length, color: '#f59e0b', bg: '#fef9c3' },
                  ].map(s => (
                    <div key={s.label} style={{ padding: '10px 18px', borderRadius: 12, background: s.bg, border: `1.5px solid ${s.color}33`, display: 'flex', alignItems: 'center', gap: 10 }}>
                      <span style={{ fontSize: 22, fontWeight: 900, color: s.color }}>{s.value}</span>
                      <span style={{ fontSize: 13, color: s.color, fontWeight: 600 }}>{s.label}</span>
                    </div>
                  ))}
                </div>

                {/* 依公司分組 */}
                {companies.filter(c => !c.archived).map(c => {
                  const members = allUsers.filter(u => u.company_id === c.id)
                  const pi = planInfo(c.plan)
                  const isOpen = userTab === c.id
                  if (members.length === 0) return null
                  return (
                    <div key={c.id} style={{ background: '#fff', borderRadius: 14, marginBottom: 10, border: isOpen ? `2px solid ${C.accent}` : '1.5px solid #e2e8f0', overflow: 'hidden', boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}>
                      {/* 公司標頭（點擊展開）*/}
                      <div style={{ display: 'flex', alignItems: 'center', gap: 12, cursor: 'pointer', padding: '16px 20px', userSelect: 'none' }}
                        onClick={() => setUserTab(isOpen ? null : c.id)}>
                        {/* 公司 Logo 圓圈 */}
                        <div style={{ width: 44, height: 44, borderRadius: 12, background: isOpen ? `linear-gradient(135deg,${C.accent},#0ea5e9)` : 'linear-gradient(135deg,#e2e8f0,#cbd5e1)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18, fontWeight: 900, color: isOpen ? '#fff' : '#64748b', flexShrink: 0, transition: 'all 0.2s' }}>
                          {c.name[0]}
                        </div>
                        <div style={{ flex: 1 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                            <span style={{ fontSize: 15, fontWeight: 800, color: '#0f172a' }}>{c.name}</span>
                            <span style={{ ...st.badge('ok'), background: pi.bg, color: pi.color, fontSize: 10, border: `1px solid ${pi.color}44` }}>{pi.label}</span>
                            {!c.is_active && <span style={{ ...st.badge(''), background: '#fee2e2', color: '#dc2626', fontSize: 10 }}>停用</span>}
                          </div>
                          <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 3, display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                            <span>👥 {members.length} 位成員</span>
                            <span>⚙️ {members.filter(u => u.role === 'admin').length} 位管理員</span>
                            {c.short_code && <span style={{ fontFamily: 'monospace', background: '#f1f5f9', padding: '1px 6px', borderRadius: 5 }}>代碼：{c.short_code}</span>}
                          </div>
                        </div>
                        {/* 成員預覽頭像 */}
                        <div style={{ display: 'flex', flexDirection: 'row-reverse', marginRight: 8 }}>
                          {members.slice(0, 5).map((u, i) => (
                            <div key={u.id} style={{ width: 30, height: 30, borderRadius: '50%', background: `hsl(${(u.name.charCodeAt(0) * 47) % 360},60%,60%)`, border: '2.5px solid #fff', marginLeft: i === 0 ? 0 : -10, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 700, color: '#fff', zIndex: members.length - i }}>
                              {u.name[0]}
                            </div>
                          ))}
                          {members.length > 5 && <div style={{ width: 30, height: 30, borderRadius: '50%', background: '#f1f5f9', border: '2.5px solid #fff', marginLeft: -10, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, fontWeight: 700, color: '#64748b', zIndex: 0 }}>+{members.length - 5}</div>}
                        </div>
                        <span style={{ fontSize: 18, color: isOpen ? C.accent : '#cbd5e1', transition: 'transform 0.2s', transform: isOpen ? 'rotate(180deg)' : 'none' }}>▼</span>
                      </div>

                      {/* 展開的成員列表 */}
                      {isOpen && (
                        <div style={{ padding: '4px 20px 20px', borderTop: '1.5px solid #f1f5f9' }}>
                          <div style={{ margin: '14px 0 10px', fontSize: 12, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: 1 }}>
                            成員列表 · {members.length} 人
                          </div>
                          {members
                            .sort((a, b) => {
                              const order = { admin: 0, manager: 1, user: 2 }
                              return (order[a.role] ?? 3) - (order[b.role] ?? 3)
                            })
                            .map(u => <UserRow key={u.id} u={u} />)}
                        </div>
                      )}
                    </div>
                  )
                })}

                {/* 未指派成員 */}
                {unassigned.length > 0 && (
                  <div style={{ background: '#fff', borderRadius: 14, marginBottom: 10, border: userTab === '__unassigned__' ? `2px solid #f59e0b` : '1.5px solid #fde68a', overflow: 'hidden' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12, cursor: 'pointer', padding: '16px 20px' }}
                      onClick={() => setUserTab(userTab === '__unassigned__' ? null : '__unassigned__')}>
                      <div style={{ width: 44, height: 44, borderRadius: 12, background: '#fef9c3', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 22, flexShrink: 0 }}>⚠️</div>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: 15, fontWeight: 800, color: '#92400e' }}>未指派公司</div>
                        <div style={{ fontSize: 12, color: '#a16207', marginTop: 2 }}>{unassigned.length} 位帳號尚未綁定公司</div>
                      </div>
                      <span style={{ fontSize: 18, color: '#f59e0b', transition: 'transform 0.2s', transform: userTab === '__unassigned__' ? 'rotate(180deg)' : 'none' }}>▼</span>
                    </div>
                    {userTab === '__unassigned__' && (
                      <div style={{ padding: '4px 20px 20px', borderTop: '1.5px solid #fef9c3' }}>
                        {unassigned.map(u => <UserRow key={u.id} u={u} />)}
                      </div>
                    )}
                  </div>
                )}

                {/* 無任何用戶 */}
                {allUsers.filter(u => !u.is_super_admin).length === 0 && (
                  <div style={{ background: '#fff', borderRadius: 14, border: '1.5px solid #e2e8f0', padding: 40 }}>
                    <EmptyState icon="👥" text="尚無用戶資料" />
                  </div>
                )}
              </div>
            )
          })()}

          {/* ── 維護排程 ── */}
          {tab === 'schedule' && (
            <div>
              {/* 新增按鈕 */}
              <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 16 }}>
                <button style={{ ...st.btn(), padding: '12px 22px', fontSize: 14 }} onClick={() => { setScheduleForm(emptySchedule()); setShowScheduleForm(true) }}>
                  ＋ 新增維護排程
                </button>
              </div>

              {/* 排程列表 */}
              {schedules.length === 0 ? (
                <div style={{ background: '#fff', borderRadius: 14, border: '1.5px solid #e2e8f0', padding: 40 }}>
                  <EmptyState icon="🗓" text="尚無維護排程" />
                </div>
              ) : schedules.map(s => {
                const st2 = schedStatus(s)
                const company = companies.find(c => c.id === s.company_id)
                const fmt = iso => new Date(iso).toLocaleString('zh-TW', { year:'numeric', month:'numeric', day:'numeric', hour:'2-digit', minute:'2-digit' })
                return (
                  <div key={s.id} style={{ background: '#fff', borderRadius: 14, border: `1.5px solid ${st2.label === '維護中' ? '#fcd34d' : '#e2e8f0'}`, marginBottom: 12, padding: '18px 22px', boxShadow: st2.label === '維護中' ? '0 0 0 3px rgba(251,191,36,0.2)' : '0 1px 4px rgba(0,0,0,0.04)' }}>
                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 14, flexWrap: 'wrap' }}>
                      {/* 左：資訊 */}
                      <div style={{ flex: 1, minWidth: 240 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6, flexWrap: 'wrap' }}>
                          <span style={{ fontSize: 15, fontWeight: 800, color: '#0f172a' }}>{s.title}</span>
                          <span style={{ fontSize: 11, padding: '2px 10px', borderRadius: 20, fontWeight: 700, background: st2.bg, color: st2.color }}>{st2.label}</span>
                          {s.notify_sent && <span style={{ fontSize: 11, padding: '2px 10px', borderRadius: 20, fontWeight: 700, background: '#f0fdf4', color: '#15803d' }}>📢 已通知</span>}
                        </div>
                        <div style={{ fontSize: 13, color: '#475569', marginBottom: 4 }}>
                          🏢 {company?.name || <span style={{ color: '#f59e0b', fontWeight: 700 }}>全部客戶</span>}
                        </div>
                        <div style={{ fontSize: 13, color: '#0f172a', fontWeight: 600, marginBottom: 4 }}>
                          🕐 {fmt(s.starts_at)} ～ {fmt(s.ends_at)}
                        </div>
                        {s.body && <div style={{ fontSize: 13, color: '#64748b', marginBottom: 4 }}>{s.body}</div>}
                        {(s.contact_name || s.contact_phone || s.contact_email) && (
                          <div style={{ fontSize: 12, color: '#64748b', background: '#f8fafc', borderRadius: 8, padding: '6px 10px', marginTop: 6, display: 'inline-flex', gap: 10, flexWrap: 'wrap' }}>
                            <span>📞 聯絡方式：</span>
                            {s.contact_name && <span>{s.contact_name}</span>}
                            {s.contact_phone && <span>{s.contact_phone}</span>}
                            {s.contact_email && <span>{s.contact_email}</span>}
                          </div>
                        )}
                      </div>
                      {/* 右：操作 */}
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 8, flexShrink: 0 }}>
                        {s.status !== 'cancelled' && (
                          <button
                            style={{ padding: '8px 16px', borderRadius: 8, border: '1.5px solid #86efac', background: s.notify_sent ? '#f8fafc' : '#f0fdf4', color: s.notify_sent ? '#94a3b8' : '#15803d', fontSize: 13, fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap' }}
                            onClick={() => handleSendMaintenanceNotify(s)}>
                            {s.notify_sent ? '↻ 重新發送通知' : '📢 發送通知給客戶'}
                          </button>
                        )}
                        {s.status !== 'cancelled' && (
                          <button
                            style={{ padding: '8px 16px', borderRadius: 8, border: '1.5px solid #93c5fd', background: '#eff6ff', color: '#1d4ed8', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}
                            onClick={() => openEditSchedule(s)}>
                            ✏️ 編輯排程
                          </button>
                        )}
                        {s.status === 'scheduled' && st2.label === '維護中' && (
                          <button
                            style={{ padding: '8px 16px', borderRadius: 8, border: '1.5px solid #86efac', background: '#f0fdf4', color: '#15803d', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}
                            onClick={() => handleCompleteSchedule(s.id)}>
                            ✓ 標記完成維護
                          </button>
                        )}
                        {s.status === 'scheduled' && (
                          <button
                            style={{ padding: '8px 16px', borderRadius: 8, border: '1.5px solid #fcd34d', background: '#fef9c3', color: '#92400e', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}
                            onClick={() => handleCancelSchedule(s.id)}>
                            取消排程
                          </button>
                        )}
                        <button
                          style={{ padding: '8px 16px', borderRadius: 8, border: '1.5px solid #fca5a5', background: '#fef2f2', color: '#dc2626', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}
                          onClick={() => handleDeleteSchedule(s.id)}>
                          刪除
                        </button>
                      </div>
                    </div>
                  </div>
                )
              })}

              {/* 新增 / 編輯排程 Modal */}
              {(showScheduleForm || editingSchedule) && (
                <Modal
                  title={editingSchedule ? `✏️ 編輯排程 — ${editingSchedule.title}` : '🗓 新增維護排程'}
                  onClose={() => { setShowScheduleForm(false); setEditingSchedule(null); setScheduleForm(emptySchedule()) }}>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                    <div>
                      <label style={st.label}>發送對象</label>
                      <select style={st.input} value={scheduleForm.company_id} onChange={e => sf('company_id', e.target.value)}>
                        <option value="">📣 全部客戶</option>
                        {activeCompanies.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                      </select>
                    </div>
                    <div>
                      <label style={st.label}>維護標題 *</label>
                      <input style={st.input} value={scheduleForm.title} onChange={e => sf('title', e.target.value)} placeholder="例：系統維護公告、資料庫升級" />
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                      <div>
                        <label style={st.label}>開始時間 *</label>
                        <input type="datetime-local" style={st.input} value={scheduleForm.starts_at} onChange={e => sf('starts_at', e.target.value)} />
                      </div>
                      <div>
                        <label style={st.label}>結束時間 *</label>
                        <input type="datetime-local" style={st.input} value={scheduleForm.ends_at} onChange={e => sf('ends_at', e.target.value)} />
                      </div>
                    </div>
                    <div>
                      <label style={st.label}>說明內容</label>
                      <textarea style={{ ...st.input, minHeight: 80, resize: 'vertical', lineHeight: 1.6 }} value={scheduleForm.body} onChange={e => sf('body', e.target.value)} placeholder="維護原因、影響範圍、建議備份說明..." />
                    </div>
                    <div style={{ padding: '10px 14px', background: '#f0f9ff', borderRadius: 10, border: '1.5px solid #bae6fd' }}>
                      <div style={{ fontSize: 12, fontWeight: 700, color: '#0369a1', marginBottom: 8 }}>📞 聯絡資訊（供客戶反映更改需求）</div>
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 10 }}>
                        <div>
                          <label style={{ ...st.label, color: '#0369a1' }}>聯絡人姓名</label>
                          <input style={st.input} value={scheduleForm.contact_name} onChange={e => sf('contact_name', e.target.value)} placeholder="王小明" />
                        </div>
                        <div>
                          <label style={{ ...st.label, color: '#0369a1' }}>電話</label>
                          <input style={st.input} value={scheduleForm.contact_phone} onChange={e => sf('contact_phone', e.target.value)} placeholder="02-xxxx-xxxx" />
                        </div>
                        <div>
                          <label style={{ ...st.label, color: '#0369a1' }}>Email</label>
                          <input style={st.input} value={scheduleForm.contact_email} onChange={e => sf('contact_email', e.target.value)} placeholder="support@ypfops.com" />
                        </div>
                      </div>
                    </div>
                    {/* 編輯時：重新通知選項 */}
                    {editingSchedule && (
                      <label style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '12px 14px', borderRadius: 10, border: `2px solid ${scheduleRenotify ? '#fcd34d' : '#e2e8f0'}`, background: scheduleRenotify ? '#fefce8' : '#f8fafc', cursor: 'pointer', transition: 'all 0.15s' }}>
                        <input type="checkbox" checked={scheduleRenotify} onChange={e => setScheduleRenotify(e.target.checked)} style={{ width: 16, height: 16, cursor: 'pointer' }} />
                        <div>
                          <div style={{ fontSize: 13, fontWeight: 700, color: scheduleRenotify ? '#92400e' : '#475569' }}>
                            🔔 儲存後立即重新通知客戶
                          </div>
                          <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 2 }}>
                            客戶將收到「維護時間變更」通知，內含更新後的時段
                          </div>
                        </div>
                      </label>
                    )}

                    <div style={{ display: 'flex', gap: 10, marginTop: 4 }}>
                      <button style={{ ...st.btn(editingSchedule && scheduleRenotify ? 'warning' : 'primary'), flex: 1, padding: '14px' }}
                        onClick={editingSchedule ? handleSaveScheduleEdit : handleCreateSchedule}
                        disabled={savingSchedule}>
                        {savingSchedule
                          ? (editingSchedule ? '更新中...' : '建立中...')
                          : editingSchedule
                            ? (scheduleRenotify ? '💾 儲存並重新通知' : '✓ 儲存變更')
                            : '✓ 建立排程'}
                      </button>
                      <button style={{ ...st.btn('default'), padding: '14px 20px' }}
                        onClick={() => { setShowScheduleForm(false); setEditingSchedule(null); setScheduleForm(emptySchedule()) }}>
                        取消
                      </button>
                    </div>
                  </div>
                </Modal>
              )}
            </div>
          )}

          {/* ── 廣播通知 ── */}
          {tab === 'broadcast' && (
            <div style={{ maxWidth: 680 }}>
              <div style={{ background: '#fff', borderRadius: 14, border: '1.5px solid #e2e8f0', padding: '24px 28px', marginBottom: 16, boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}>
                <div style={{ fontSize: 16, fontWeight: 800, color: '#0f172a', marginBottom: 6 }}>📢 發送系統通知</div>
                <div style={{ fontSize: 13, color: '#64748b', marginBottom: 20 }}>通知會即時出現在用戶的鈴鐺圖示，並可選擇同時推播手機通知</div>

                <label style={st.label}>發送對象</label>
                <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
                  {[{ k: 'all', label: '📣 全部用戶' }, { k: 'company', label: '🏢 指定公司' }].map(o => (
                    <button key={o.k} type="button"
                      style={{ padding: '10px 18px', borderRadius: 10, fontSize: 13, fontWeight: 700, cursor: 'pointer', border: `2px solid ${broadcastForm.target === o.k ? '#3b82f6' : '#e2e8f0'}`, background: broadcastForm.target === o.k ? '#eff6ff' : '#fff', color: broadcastForm.target === o.k ? '#1d4ed8' : '#64748b' }}
                      onClick={() => setBroadcastForm(f => ({ ...f, target: o.k, company_id: '' }))}>
                      {o.label}
                    </button>
                  ))}
                </div>

                {broadcastForm.target === 'company' && (
                  <div style={{ marginBottom: 16 }}>
                    <label style={st.label}>選擇公司</label>
                    <select style={st.input} value={broadcastForm.company_id} onChange={e => setBroadcastForm(f => ({ ...f, company_id: e.target.value }))}>
                      <option value="">— 請選擇 —</option>
                      {activeCompanies.map(c => (
                        <option key={c.id} value={c.id}>{c.name}（{allUsers.filter(u => u.company_id === c.id).length} 人）</option>
                      ))}
                    </select>
                  </div>
                )}

                <label style={st.label}>通知標題 *</label>
                <input style={{ ...st.input, marginBottom: 14 }} value={broadcastForm.title}
                  placeholder="例：系統維護通知、重要更新公告"
                  onChange={e => setBroadcastForm(f => ({ ...f, title: e.target.value }))} />

                <label style={st.label}>通知內容 *</label>
                <textarea style={{ ...st.input, minHeight: 100, resize: 'vertical', marginBottom: 20, lineHeight: 1.6 }}
                  value={broadcastForm.body}
                  placeholder="填寫通知詳細內容..."
                  onChange={e => setBroadcastForm(f => ({ ...f, body: e.target.value }))} />

                <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
                  <button style={{ ...st.btn(), padding: '13px 28px', fontSize: 14 }} onClick={handleBroadcast} disabled={broadcasting}>
                    {broadcasting ? '發送中...' : '📢 立即發送'}
                  </button>
                  <div style={{ fontSize: 12, color: '#64748b' }}>
                    預計發送給：
                    <strong style={{ color: '#0f172a', marginLeft: 4 }}>
                      {broadcastForm.target === 'all'
                        ? `${allUsers.filter(u => !u.is_super_admin && u.approved !== false).length} 位用戶`
                        : broadcastForm.company_id
                          ? `${allUsers.filter(u => u.company_id === broadcastForm.company_id && u.approved !== false).length} 位用戶`
                          : '— 請選擇公司'}
                    </strong>
                  </div>
                </div>
              </div>

              {/* 使用說明 */}
              <div style={{ background: '#f0f9ff', border: '1.5px solid #bae6fd', borderRadius: 12, padding: '14px 18px' }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: '#0369a1', marginBottom: 8 }}>💡 使用說明</div>
                <div style={{ fontSize: 12, color: '#0369a1', lineHeight: 1.9 }}>
                  • 通知會即時出現在用戶的 🔔 鈴鐺圖示<br />
                  • 用戶須開啟瀏覽器推播許可才能收到手機通知橫幅<br />
                  • 建議用於：系統維護公告、重要更新、緊急通知
                </div>
              </div>
            </div>
          )}

          {/* ── 操作日誌 ── */}
          {tab === 'logs' && (
            <div style={{ background: '#fff', borderRadius: 14, border: '1.5px solid #e2e8f0', overflow: 'hidden', boxShadow: '0 1px 4px rgba(0,0,0,0.05)' }}>
              <ScrollTable>
                <thead>
                  <tr style={{ background: '#f8fafc' }}>
                    {['時間', '公司', '操作人', '動作', '說明'].map(h => (
                      <th key={h} style={{ ...st.th, background: 'transparent', color: '#475569' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {allLogs.length === 0 ? (
                    <tr><td colSpan={5}><EmptyState icon="📋" text="尚無日誌" /></td></tr>
                  ) : allLogs.map(log => {
                    const company = companies.find(c => c.id === log.company_id)
                    return (
                      <tr key={log.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                        <td style={{ ...st.td, fontSize: 12, color: '#94a3b8', whiteSpace: 'nowrap' }}>{formatDate(log.created_at)}</td>
                        <td style={{ ...st.td, fontSize: 13, color: '#475569' }}>{company?.name || '—'}</td>
                        <td style={{ ...st.td, fontWeight: 600, color: '#0f172a' }}>{log.user_name || log.operator || '—'}</td>
                        <td style={st.td}><span style={{ ...st.badge('ok'), fontSize: 11 }}>{log.action}</span></td>
                        <td style={{ ...st.td, color: '#64748b', fontSize: 13 }}>{log.detail}</td>
                      </tr>
                    )
                  })}
                </tbody>
              </ScrollTable>
            </div>
          )}

        </div>
      </div>

      {/* ── 封存確認 Modal ── */}
      {archivingCompany && (
        <Modal title={`🗄️ 封存客戶 — ${archivingCompany.name}`} onClose={() => setArchivingCompany(null)}>
          <div style={{ padding: '14px 16px', background: '#f8fafc', borderRadius: 10, border: '1.5px solid #e2e8f0', marginBottom: 20 }}>
            <div style={{ fontSize: 14, fontWeight: 700, color: '#334155', marginBottom: 6 }}>封存說明</div>
            <div style={{ fontSize: 13, color: '#64748b', lineHeight: 1.8 }}>
              • 客戶帳號將被<strong>停用</strong>，無法再登入<br />
              • 所有資料<strong>保留不刪除</strong><br />
              • 隨時可從「封存客戶」列表取消封存、恢復帳號<br />
              • 封存 ≠ 刪除，資料完整保存
            </div>
          </div>
          <label style={st.label}>封存原因 <span style={{ color: '#94a3b8', fontWeight: 400 }}>(選填，方便日後查閱)</span></label>
          <textarea
            style={{ ...st.input, height: 90, resize: 'vertical', fontFamily: 'inherit', lineHeight: 1.6, marginBottom: 8 }}
            value={archiveReason}
            placeholder="例如：合約到期未續約、客戶倒閉、暫停使用..."
            onChange={e => setArchiveReason(e.target.value)}
          />
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginTop: 16 }}>
            <button style={{ ...st.btn('default'), padding: '13px' }} onClick={() => setArchivingCompany(null)}>取消</button>
            <button
              style={{ padding: '13px', borderRadius: 10, border: '2px solid #64748b', background: '#f1f5f9', color: '#334155', fontSize: 14, fontWeight: 800, cursor: 'pointer' }}
              onClick={handleArchive}>
              🗄️ 確認封存
            </button>
          </div>
        </Modal>
      )}

      {/* ── 新增客戶 Modal ── */}
      {showNewCustomer && (
        <Modal title="＋ 新增客戶" onClose={() => setShowNewCustomer(false)}>
          <div style={{ padding: '8px 12px', background: C.accentDim, borderRadius: 8, marginBottom: 14 }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: C.accent }}>🏢 公司資料</div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 14, marginBottom: 20 }}>
            <div><label style={st.label}>公司名稱 *</label><input style={st.input} value={newCustomer.name} placeholder="XX 有限公司" onChange={e => nc('name', e.target.value)} autoFocus /></div>
            <div><label style={st.label}>統一編號</label><input style={st.input} value={newCustomer.tax_id} placeholder="12345678" onChange={e => nc('tax_id', e.target.value.replace(/\D/g, '').slice(0, 8))} /></div>
            <div>
              <label style={st.label}>識別碼 * <span style={{ color: C.muted, fontWeight: 400 }}>(英文小寫)</span></label>
              <input style={st.input} value={newCustomer.slug} placeholder="company-name" onChange={e => nc('slug', e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))} />
            </div>
            <div>
              <label style={st.label}>員工登入代碼 * <span style={{ color: C.muted, fontWeight: 400 }}>(2~12碼英數字，發給員工用)</span></label>
              <input style={st.input} value={newCustomer.short_code} placeholder="abc123" onChange={e => nc('short_code', e.target.value.toLowerCase().replace(/[^a-z0-9]/g, '').slice(0, 12))} />
              {newCustomer.short_code && <div style={{ fontSize: 11, color: C.muted, marginTop: 4 }}>員工登入格式：工號 + 代碼「{newCustomer.short_code}」+ 密碼</div>}
            </div>
            <div><label style={st.label}>負責人 Email</label><input style={st.input} value={newCustomer.contact_email} placeholder="owner@company.com" onChange={e => nc('contact_email', e.target.value)} /></div>
            <div><label style={st.label}>聯絡電話</label><input style={st.input} value={newCustomer.contact_phone} placeholder="02-xxxx-xxxx" onChange={e => nc('contact_phone', e.target.value)} /></div>
            <div><label style={st.label}>最多用戶數</label><input type="number" min="1" style={st.input} value={newCustomer.max_users} onChange={e => nc('max_users', parseInt(e.target.value) || 10)} /></div>
            <div style={{ gridColumn: 'span 2' }}><label style={st.label}>地址</label><input style={st.input} value={newCustomer.address} placeholder="公司地址" onChange={e => nc('address', e.target.value)} /></div>
            <div style={{ gridColumn: 'span 2' }}><label style={st.label}>備註</label><input style={st.input} value={newCustomer.notes} placeholder="客戶備註、聯繫紀錄..." onChange={e => nc('notes', e.target.value)} /></div>
          </div>

          <div style={{ padding: '8px 12px', background: C.yellowBg, borderRadius: 8, marginBottom: 14 }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: C.yellow }}>💳 訂閱方案</div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 14, marginBottom: 20 }}>
            <div>
              <label style={st.label}>方案</label>
              <select style={st.input} value={newCustomer.plan} onChange={e => nc('plan', e.target.value)}>
                {PLANS.filter(p => p.key !== 'suspended').map(p => <option key={p.key} value={p.key}>{p.label}</option>)}
              </select>
            </div>
            <div>
              <label style={st.label}>到期日</label>
              <input type="date" style={st.input} value={newCustomer.plan_expires_at} onChange={e => nc('plan_expires_at', e.target.value)} />
            </div>
          </div>

          <div style={{ padding: '8px 12px', background: C.greenBg, borderRadius: 8, marginBottom: 14 }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: C.green }}>🔑 管理員帳號（系統登入用）</div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 14, marginBottom: 24 }}>
            <div><label style={st.label}>管理員姓名 *</label><input style={st.input} value={newCustomer.admin_name} placeholder="王大明" onChange={e => nc('admin_name', e.target.value)} /></div>
            <div><label style={st.label}>登入 Email *</label><input style={st.input} value={newCustomer.admin_email} placeholder="admin@company.com" onChange={e => nc('admin_email', e.target.value)} /></div>
            <div style={{ gridColumn: 'span 2' }}>
              <label style={st.label}>預設密碼 * <span style={{ color: C.muted, fontWeight: 400 }}>(6–12 位英數字，首次登入需更改)</span></label>
              <div style={{ display: 'flex', gap: 10 }}>
                <input style={{ ...st.input, flex: 1, fontFamily: 'monospace', letterSpacing: 3, fontSize: 16 }}
                  value={newCustomer.admin_password} onChange={e => nc('admin_password', e.target.value.replace(/[^a-zA-Z0-9]/g, ''))} />
                <button style={{ ...st.btn('default'), padding: '0 16px', minHeight: 52, fontSize: 13, whiteSpace: 'nowrap' }}
                  onClick={() => nc('admin_password', genPassword())}>⚡ 隨機</button>
              </div>
            </div>
          </div>
          <div style={{ display: 'flex', gap: 10 }}>
            <button style={{ ...st.btn(), flex: 1, padding: '15px', fontSize: 15 }} onClick={handleCreateNewCustomer} disabled={creating}>
              {creating ? '建立中...' : '✓ 建立客戶'}
            </button>
            <button style={{ ...st.btn('default'), padding: '15px 20px' }} onClick={() => setShowNewCustomer(false)}>取消</button>
          </div>
        </Modal>
      )}

      {/* ── 編輯公司 Modal ── */}
      {editingCompany && (
        <Modal title={`✏️ 編輯 — ${editingCompany.name}`} onClose={() => setEditingCompany(null)}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 14 }}>
            {[['name','公司名稱 *'],['tax_id','統一編號'],['slug','識別碼 *'],['contact_email','負責人 Email'],['contact_phone','聯絡電話'],['address','地址'],['notes','備註']].map(([k, label]) => (
              <div key={k} style={{ gridColumn: ['address','notes'].includes(k) ? 'span 2' : undefined }}>
                <label style={st.label}>{label}</label>
                <input style={st.input} value={editForm[k] || ''} onChange={e => setEditForm(f => ({ ...f, [k]: e.target.value }))} />
              </div>
            ))}
            <div>
              <label style={st.label}>方案</label>
              <select style={st.input} value={editForm.plan || 'trial'} onChange={e => setEditForm(f => ({ ...f, plan: e.target.value }))}>
                {PLANS.map(p => <option key={p.key} value={p.key}>{p.label}</option>)}
              </select>
            </div>
            <div>
              <label style={st.label}>到期日</label>
              <input type="date" style={st.input} value={editForm.plan_expires_at || ''} onChange={e => setEditForm(f => ({ ...f, plan_expires_at: e.target.value }))} />
            </div>
            <div>
              <label style={st.label}>最多用戶數</label>
              <input type="number" min="1" style={st.input} value={editForm.max_users || 10} onChange={e => setEditForm(f => ({ ...f, max_users: parseInt(e.target.value) || 10 }))} />
            </div>
            <div>
              <label style={st.label}>狀態</label>
              <select style={st.input} value={editForm.is_active ? '1' : '0'} onChange={e => setEditForm(f => ({ ...f, is_active: e.target.value === '1' }))}>
                <option value="1">✅ 啟用</option>
                <option value="0">🔴 停用</option>
              </select>
            </div>
          </div>
          <div style={{ display: 'flex', gap: 10, marginTop: 20 }}>
            <button style={{ ...st.btn(), flex: 1, padding: '14px' }} onClick={handleSaveEdit} disabled={savingEdit}>
              {savingEdit ? '更新中...' : '確認更新'}
            </button>
            <button style={{ ...st.btn('default'), padding: '14px 20px' }} onClick={() => setEditingCompany(null)}>取消</button>
          </div>
        </Modal>
      )}

      {/* ── 刪除客戶確認 Modal ── */}
      {deletingCompany && (
        <Modal title={`🗑 永久刪除 — ${deletingCompany.name}`} onClose={() => setDeletingCompany(null)}>
          <div style={{ padding: '14px 16px', background: '#fef2f2', borderRadius: 10, border: '2px solid #fca5a5', marginBottom: 20 }}>
            <div style={{ fontSize: 14, fontWeight: 800, color: '#dc2626', marginBottom: 8 }}>⚠️ 此操作無法復原</div>
            <div style={{ fontSize: 13, color: '#7f1d1d', lineHeight: 1.8 }}>
              • 公司資料將<strong>永久刪除</strong><br />
              • 所有員工帳號將被<strong>解除公司綁定</strong><br />
              • 操作日誌、通知等歷史資料也會一併刪除<br />
              • 建議只在客戶明確要求時執行
            </div>
          </div>
          <label style={st.label}>請輸入公司名稱確認：<strong style={{ color: '#dc2626' }}>{deletingCompany.name}</strong></label>
          <input style={{ ...st.input, marginBottom: 20, borderColor: deleteConfirmName === deletingCompany.name ? '#22c55e' : '#e2e8f0' }}
            value={deleteConfirmName}
            placeholder="輸入公司名稱確認刪除"
            onChange={e => setDeleteConfirmName(e.target.value)} />
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            <button style={{ ...st.btn('default'), padding: '13px' }} onClick={() => setDeletingCompany(null)}>取消</button>
            <button style={{ ...st.btn('danger'), padding: '13px', opacity: deleteConfirmName === deletingCompany.name ? 1 : 0.4 }}
              onClick={handleDeleteCompany} disabled={deleting || deleteConfirmName !== deletingCompany.name}>
              {deleting ? '刪除中...' : '🗑 確認永久刪除'}
            </button>
          </div>
        </Modal>
      )}

      {/* Toast */}
      {toast && (
        <div style={{ position: 'fixed', bottom: 28, right: 28, zIndex: 9999, padding: '14px 22px', borderRadius: 12, fontWeight: 700, fontSize: 14, boxShadow: '0 4px 20px rgba(0,0,0,0.18)', background: toast.type === 'error' ? '#fef2f2' : '#f0fdf4', color: toast.type === 'error' ? '#dc2626' : '#15803d', border: `2px solid ${toast.type === 'error' ? '#fca5a5' : '#86efac'}` }}>
          {toast.msg}
        </div>
      )}

      {/* ── 公司詳情 Modal ── */}
      {selectedCompany && (
        <Modal title={`🏢 ${selectedCompany.name}`} onClose={() => setSelectedCompany(null)}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 20 }}>
            {[['識別碼', selectedCompany.slug], ['統一編號', selectedCompany.tax_id || '—'],
              ['方案', planInfo(selectedCompany.plan).label],
              ['到期日', selectedCompany.plan_expires_at ? formatDate(selectedCompany.plan_expires_at) : '無限制'],
              ['聯絡 Email', selectedCompany.contact_email || '—'],
              ['電話', selectedCompany.contact_phone || '—'],
            ].map(([label, value]) => (
              <div key={label} style={{ padding: '10px 14px', background: '#f8fafc', borderRadius: 10, border: '1.5px solid #e2e8f0' }}>
                <div style={{ fontSize: 11, color: '#64748b', marginBottom: 3, fontWeight: 600 }}>{label}</div>
                <div style={{ fontSize: 14, color: '#0f172a', fontWeight: 700 }}>{value}</div>
              </div>
            ))}
          </div>
          <div style={{ fontSize: 14, fontWeight: 700, color: '#0f172a', marginBottom: 10 }}>👥 公司用戶（{companyUsers.length} 人）</div>
          {companyUsers.map(u => (
            <div key={u.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 14px', background: '#f8fafc', borderRadius: 10, marginBottom: 8, gap: 10, flexWrap: 'wrap' }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 14, fontWeight: 700, color: '#0f172a' }}>{u.name}</div>
                <div style={{ fontSize: 12, color: '#64748b' }}>{u.email} · {u.position || '未設職位'}</div>
              </div>
              <div style={{ display: 'flex', gap: 6, flexShrink: 0, flexWrap: 'wrap', alignItems: 'center' }}>
                {u.role === 'admin'
                  ? <span style={{ ...st.badge('ok'), fontSize: 11 }}>⚙️ 管理員</span>
                  : <button style={{ ...st.btn('primary'), padding: '5px 10px', fontSize: 11, minHeight: 30 }} onClick={() => handleSetAdmin(u.id)}>設為管理員</button>}
                <button style={{ ...st.btn('default'), padding: '5px 10px', fontSize: 11, minHeight: 30, background: '#0f172a', color: '#38bdf8', border: '1.5px solid #38bdf844' }}
                  onClick={() => handleImpersonate(u.id, u.name)} disabled={impersonating}>
                  🔑 代理登入
                </button>
              </div>
            </div>
          ))}
          {companyLogs.length > 0 && (
            <>
              <div style={{ fontSize: 14, fontWeight: 700, color: '#0f172a', margin: '16px 0 10px' }}>📋 最近操作</div>
              {companyLogs.slice(0, 8).map(log => (
                <div key={log.id} style={{ display: 'flex', gap: 10, padding: '7px 0', borderBottom: '1px solid #f1f5f9', fontSize: 13 }}>
                  <span style={{ color: '#94a3b8', minWidth: 110, flexShrink: 0 }}>{formatDate(log.created_at)}</span>
                  <span style={{ fontWeight: 600, color: '#0f172a' }}>{log.user_name || log.operator}</span>
                  <span style={{ color: C.accent }}>{log.action}</span>
                </div>
              ))}
            </>
          )}
        </Modal>
      )}
    </div>
  )
}
